/**
 * #988 — the Polymarket US manual ticket: pick an exact market and outcome,
 * enter an order, see its cost, place it with one click, and keep an
 * attributable receipt. It behaves like the Kalshi/Polymarket ticket: one
 * "Place Order" button, with Total Cost (fee) / Max Payout / Max Profit (or
 * Proceeds for a sell) computed locally before the click.
 *
 * Fits `UsPortfolioWorkspace.renderTicket` as `(mount) => <UsManualTicket
 * {...mount} trading={…} markets={…} />`. The workspace remounts it on every
 * account visit; the ticket also resets itself if handed a different account
 * or scope, so an old account's order or result can never paint into a new one.
 *
 * One click is one action with two steps, run back to back:
 * 1. The relay checks the exact order (`preview()`; no order is sent). The
 *    answer must name this attempt's draft revision and scope.
 * 2. That checked order is sent once (`submit()`). The trading client freezes
 *    the exact terms under a saved request key, so a lost response can be
 *    recovered from "Your saved US requests" instead of resent.
 *
 * Identity rules:
 * - Every click mints a new draft revision. An edit, target change, rules
 *   revision change, account/scope change or access change during step 1
 *   drops the attempt: a late answer for it is never sent, and the ticket
 *   says nothing was sent.
 * - Repeat clicks while an attempt is running do nothing.
 * - Once `submit()` is called the client's draft is left alone until it
 *   settles: the real client treats a new draft as a context change.
 * - Nothing here retries or resubmits a request. A request whose server
 *   receipt is shown here is acknowledged, so this browser's saved-request
 *   reminders remain only for lost responses.
 *
 * Edit mode (#1017, `editOrder`): US has no replace route, so "Replace order"
 * is one click that checks the new draft, cancels the original, and places the
 * replacement only once a read-back shows the original no longer rests and no
 * fill landed meanwhile (`ticketEdit.ts`). Any other cancel outcome places
 * nothing and says so; an unconfirmed cancel can only be checked, never
 * repeated here. From the cancel on, the entry is frozen: the checked draft is
 * the one that gets placed.
 */
import { usEvidenceNow, type UsTimeEvidence } from '@/lib/polymarketUs/timeEvidence';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Tooltip } from '@/components/atoms/Tooltip';
import { UsTicketNotice } from './UsTicketNotice';
import { InstitutionalDraftCheck } from './InstitutionalDraftCheck';
import type { InspectInstitutionalDraft } from '@/lib/polymarketUs/institutionalDraftClient';
import { UsTicketLoading } from './UsTicketLoading';
import { cn } from '@/lib/infra/utils';
import { centsToUsd } from '@/lib/polymarketUs/gameMapping';
import type { UsAccountContext } from '@/lib/polymarketUs/accountClient';
import type { UsSelectedMarket } from '@/lib/polymarketUs/marketClient';
import {
  accountBlockers,
  buildDraft,
  checkEntry,
  EMPTY_ENTRY,
  estimateUsOrderEconomics,
  formatUsdCents,
  closeQuantityFor,
  closeSellAmount,
  decimalUnits,
  draftTermsOf,
  GTD,
  gtsAllowedFor,
  isClean,
  lowestValidPrice,
  startExpiryText,
  type UsDraftTerms,
  type UsOrderEconomics,
  type UsTicketEntry,
  type UsTicketField,
} from '@/lib/polymarketUs/ticketModel';
import {
  editBlocker,
  editCancelFailure,
  editCancelSettlement,
  editEntryFrom,
  editOrderSettlement,
  editPostOnlyHelp,
  editTermNotes,
  editTermsHeader,
  editUnchanged,
  editUnknownTerms,
  type UsEditSettlement,
} from '@/lib/polymarketUs/ticketEdit';
import { usOrderReason, usReasonStatus } from '@/lib/polymarketUs/reason';
import type { UsCloseEvent, UsDraft, UsOrderRow, UsPreview, UsTarget } from '@/lib/polymarketUs/types';
import { americanOdds } from '@/views/ValueDashboard/tradeModalBook';
import type { DefaultExpiryMode } from '@/stores/displaySettings';
import { PortfolioVenueBadge } from '../usVenueLabel';
import type { UsTicketCancel, UsTicketMarkets, UsTicketOrderStatus, UsTicketResult, UsTicketTrading } from './ticketAdapter';
import { marketTitle, UsMarketPicker } from './UsMarketPicker';
import { outcomeName, UsTicketFields } from './UsTicketFields';
import { UsReceiptLine, UsTicketReceipt } from './UsTicketReceipt';
import { US_TERMINAL_LIFECYCLE, usdToCentsText } from './usReceiptPresentation';
import { playUsPlacedTone, playUsRejectedTone } from './usTicketTones';

/** A market and outcome the host has already bound (Game Detail, #987).
 * `nativeOutcomeLabel` (#1030): the venue's own name the host bound the
 * outcome by (a total's "Over"/"Under"); a market that names that outcome
 * otherwise, or not at all, takes no new order here. */
export type UsLockedTarget = { marketSlug: string; outcome: 'yes' | 'no'; nativeOutcomeLabel?: string };

/** A host's current market evidence; absence leaves standalone behavior intact. */
export type UsHostMarketReadiness =
  | { state: 'current'; expiresAtMs: number; timeEvidence?: UsTimeEvidence }
  | { state: 'unavailable'; reason: string };

function hostMarketBlocker(readiness: UsHostMarketReadiness | undefined, now: number): string | null {
  if (!readiness) return null;
  if (readiness.state === 'unavailable') return readiness.reason;
  return readiness.expiresAtMs <= usEvidenceNow(readiness.timeEvidence, now) ? 'No current US book. New orders are disabled.' : null;
}

export type UsRailControls = {
  side: 'yes' | 'no'; onSideChange(side: 'yes' | 'no'): void; startTimeMs: number | null;
  /** #1247, as the Kalshi ticket's "Price (¢) · POR": the team the price is quoted for. */
  priceLabelSuffix?: string;
  /** #1247, as K/P's "(GSW wins)" under BUY YES / BUY NO: what the selected side
   * backs. `title` names the native binding (YES team, US outcome). */
  sideCaption?: { text: string; title?: string };
  /** #1247: the account the ticket trades on, as a compact chip in the Order
   * Entry row. Provenance only: every account guard stays in the ticket. */
  accountChip?: ReactNode;
};

type Props = {
  railControls?: UsRailControls;
  context: UsAccountContext;
  /** Workspace hook: refresh saved-request reminders after a submit settles. */
  onRequestRecorded(): void;
  trading: UsTicketTrading;
  markets: UsTicketMarkets;
  inspectInstitutionalDraft?: InspectInstitutionalDraft;
  clock?: () => number;
  /** Host-bound target: no market picker and no outcome choice here. */
  lockedTarget?: UsLockedTarget;
  /** A price (cents, may be a half cent) the host picked, e.g. a ladder click. */
  priceHintCents?: number | null;
  /** Identifies the pick: a new value re-applies `priceHintCents` even when the cents are unchanged. */
  priceHintSeq?: string | null;
  hostMarketReadiness?: UsHostMarketReadiness;
  /** `rail` is the narrow Game Detail column: one column, no account header. */
  layout?: 'page' | 'rail';
  /** Default and quick sizes from the trader's preferences. */
  sizePresets?: { defaultContracts: number; quickSizes: readonly number[] };
  /** The rail's duration default (Settings → Preferences, Polymarket US). Absent: the trader chooses. */
  defaultExpiryMode?: DefaultExpiryMode;
  /** The host's memory of this trader's terms for this market (a side flip or
   * venue-tab round trip remounts the body): the rail seeds from it. The price
   * is never part of it. */
  draftTerms?: UsDraftTerms | null;
  /** Told the terms after every edit, so the host can keep them. */
  onDraftTermsChange?(terms: UsDraftTerms): void;
  /** #1017: replace this resting order (cancel, confirm, then place). Needs `cancel`. */
  editOrder?: UsOrderRow;
  cancel?: UsTicketCancel;
  /** #1247: reads the last submission back so its receipt follows the order
   * (resting, partly filled, filled, canceled). Absent: the receipt stays as sent. */
  orderStatus?: UsTicketOrderStatus;
  /** #1006: close this holding of the locked target, like international
   * Polymarket's Close. The ticket starts as Sell (locked) of `quantity`
   * contracts and, once the market's rules load, presets an immediate duration
   * at the lowest tick: it fills what the bid book has and cancels the rest,
   * so nothing rests. Every field stays visible and editable before the one
   * Place Order click; nothing retries. Needs `lockedTarget`. */
  closeHolding?: { quantity: string };
  /** #1006: where this close's one order stands (see UsCloseEvent). */
  onCloseSent?(event: UsCloseEvent): void;
  /** #1006: an edit's replacement was sent under this intent (not refused,
   * aborted or rejected) — the Tracker moves the order's pin to it. */
  onEditReplaced?(intentId: string): void;
  /** #1006: true while a cancel or submit is in flight, and for an edit from
   * its confirmed cancel until the replacement's outcome is known (its re-check
   * included); a host window should not be dismissed then (closing it would
   * drop the outcome). */
  onCommittedChange?(committed: boolean): void;
};

type BodyProps = Props & {
  /** The body is going away while its edit's outcome is still open. */
  onEditInterrupted(): void;
  /** A new edit's cancel is going out: an earlier interruption is history. */
  onEditStarted(): void;
  /** The body is going away while its order is out (a host flip or account change mid-send). */
  onSendInterrupted(): void;
  /** A new attempt is starting: an earlier interrupted send is history. */
  onSendStarted(): void;
};

export function UsManualTicket(props: Props) {
  const { accountId, scope } = props.context;
  const { editOrder } = props;
  // An edit's market and outcome are the order's own.
  const locked = editOrder ? { marketSlug: editOrder.target.marketSlug, outcome: editOrder.target.outcome } : props.lockedTarget;
  // A different account, scope or bound target is a different ticket, never
  // an edit of this one: its entry, attempt and receipt start fresh.
  const target = locked ? `|${locked.marketSlug}|${locked.outcome}` : '';
  const edit = editOrder ? `|edit|${editOrder.intentId}` : props.closeHolding ? `|close|${props.closeHolding.quantity}` : '';
  // Kept above the keyed body: an account change mid-edit remounts the body
  // and loses what its cancel did, so the trader is told to check the order.
  const [interrupted, setInterrupted] = useState(false);
  // Likewise for a plain order: the host's side flip or account change while
  // the submit is out remounts the body, and the receipt would land nowhere.
  const [sendInterrupted, setSendInterrupted] = useState(false);
  return (
    <>
      {sendInterrupted && (
        <p role="alert" data-testid="us-ticket-send-interrupted" className="mb-2 break-words text-xs text-amber-500">
          Order sent, but the ticket changed before its result arrived, so the result is not shown here.
          Check Your saved US requests before placing another.
        </p>
      )}
      {interrupted && (
        <p role="alert" data-testid="us-ticket-edit-interrupted" className="mb-2 break-words text-xs text-amber-500">
          Edit stopped: the ticket changed (account, scope or order). The original order may have been canceled,
          and no replacement was confirmed here. Check the order and your saved US requests before placing another.
        </p>
      )}
      <TicketBody key={`${accountId}|${scope.scopeKey}|${scope.revision}${target}${edit}`} {...props}
        lockedTarget={locked} onEditInterrupted={() => setInterrupted(true)} onEditStarted={() => setInterrupted(false)}
        onSendInterrupted={() => setSendInterrupted(true)} onSendStarted={() => setSendInterrupted(false)} />
    </>
  );
}

/** What the close does with the duration now chosen (#1006): true for each. */
function closeDurationNote(timeInForce: string | null): string {
  if (timeInForce === 'TIME_IN_FORCE_IMMEDIATE_OR_CANCEL') {
    return 'Immediate or cancel: it fills what the bid book has at or above the price and cancels the rest, so nothing rests.';
  }
  if (timeInForce === 'TIME_IN_FORCE_FILL_OR_KILL') {
    return 'Fill or kill: it sells the whole size at or above the price at once, or nothing; nothing rests.';
  }
  return 'Choose a duration. With immediate or cancel or fill or kill nothing rests; any other duration leaves a sell order resting.';
}

/** The close preset's duration: immediate-or-cancel, else fill-or-kill, when
 * the market offers one; otherwise none is preset and the trader chooses. */
function closeImmediateDuration(offered: readonly string[]): string | null {
  return ['TIME_IN_FORCE_IMMEDIATE_OR_CANCEL', 'TIME_IN_FORCE_FILL_OR_KILL'].find((d) => offered.includes(d)) ?? null;
}

type MarketState =
  | { status: 'none' }
  | { status: 'loading'; slug: string; market: UsSelectedMarket | null }
  | { status: 'ready'; slug: string; market: UsSelectedMarket }
  | { status: 'error'; slug: string; market: UsSelectedMarket | null; reason: string };

/**
 * `checking`: the relay is checking the order (nothing sent). `canceling`: an
 * edit's cancel (or its status check) is out. `sending`: submit() was called.
 */
type Stage = 'idle' | 'checking' | 'canceling' | 'sending';

/** Where an edit stands. Only `ready` and `refused` leave the original untouched. */
type EditPhase =
  | { kind: 'ready' }
  | Exclude<UsEditSettlement, { state: 'settled' }> & { kind: 'settlement' }
  /** The original is confirmed gone; the replacement has not been sent. `code` says why not. */
  | { kind: 'canceled'; code: string | null }
  /** The original is confirmed gone; the replacement is being re-checked or sent. */
  | { kind: 'placing' }
  /** submit() was called for the replacement; its receipt shows the result.
   * `sent`: a receipt shows the venue has it, with no lookup uncertainty. */
  | { kind: 'replaced'; sent: boolean };

/** Why the last click did not produce a receipt. */
type Failure =
  | { kind: 'not-sent'; code: string }
  | { kind: 'unconfirmed'; code: string }
  /** The ticket changed while the order was being checked; it was dropped unsent. */
  | { kind: 'superseded' }
  /** An edit stopped before its cancel: the browser could not save the replacement. */
  | { kind: 'edit-not-started'; code: string };

const ALL_FIELDS: UsTicketField[] = ['outcome', 'action', 'quantity', 'price', 'duration', 'expiry', 'postOnly'];
const FIELD_OF: Record<keyof UsTicketEntry, UsTicketField> = {
  outcome: 'outcome', action: 'action', quantity: 'quantity', outcomePrice: 'price',
  timeInForce: 'duration', expiry: 'expiry', postOnly: 'postOnly',
};
const reasonOf = (e: unknown, fallback: string) =>
  e && typeof e === 'object' && 'code' in e && typeof e.code === 'string' && /^(US|RELAY)_[A-Z0-9_]{1,100}$/.test(e.code)
    ? e.code
    : fallback;

/** Refusals the trading client raises before any request leaves the browser.
 * Anything else (notably a context change after sending) may have reached the
 * venue, so it is reported as unconfirmed, never as "not sent". */
const NOT_SENT = new Set([
  'US_CLIENT_PREVIEW_REQUIRED', 'US_CLIENT_DRAFT_REQUIRED', 'US_CLIENT_CONFIRMATION_CONSUMED',
  'US_CLIENT_RECOVERY_QUEUE_FULL', 'US_CLIENT_REQUEST_KEY_REUSED', 'US_CLIENT_INVALID_REQUEST_KEY',
  'US_CLIENT_RECOVERY_STORAGE_UNAVAILABLE', 'US_CLIENT_RECOVERY_STORAGE_INVALID',
  'US_CLIENT_AUTH_REQUIRED', 'US_CLIENT_ACCOUNT_REQUIRED',
]);

const ACTION = 'bg-primary text-primary-foreground hover:bg-primary/90 mt-2 flex w-full items-center justify-center rounded-md py-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const STAGE_LABEL: Record<Stage, string> = {
  idle: 'Replace order', checking: 'Checking…', canceling: 'Canceling original…', sending: 'Placing replacement…',
};

/** #1247: when the ticket re-reads its last submission (ms after the previous read). */
const FOLLOW_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];

const FEE_TITLE = 'Estimated taker fee at the limit price; makers may pay less. The venue charges the actual fee.';
/** One-sentence quick references for the ticket's actions; they never carry state. */
const HELP = {
  place: 'Checks the order with Polymarket US, then sends it once; nothing retries.',
  replace: 'Cancels, confirms the original stopped resting, then places with the terms shown. Terms start from the original.',
  placeReplacement: 'The original is canceled; this places the replacement once. Nothing is retried.',
  checkStatus: 'Reads the order; never cancels or resends.',
  refreshMarket: 'Re-reads tick, minimum and durations for this market.',
};

/** A full-width ticket action with its quick reference (the Tooltip atom). */
function ActionHelp({ help, children, openOnFocus = true }: { help: string; children: ReactNode; openOnFocus?: boolean }) {
  return (
    <Tooltip content={help} wrapperClassName="relative flex w-full" className="w-64 whitespace-normal" openOnFocus={openOnFocus}>{children}</Tooltip>
  );
}

/** Where a close's one order stands, from its receipt (#1006). */
type CloseOutcome = 'sent' | 'recorded' | 'unconfirmed';
const CLOSE_LINE: Record<CloseOutcome, string> = {
  sent: 'Close sent.',
  recorded: 'Close recorded but not sent. Nothing sold; you can close again once this shows Not sent.',
  unconfirmed: 'Close result unconfirmed: it may have sold. Check Your saved US requests before closing again.',
};
const CLOSE_HELP = 'Sent = Polymarket US acknowledged it. Recorded = saved by G1, never dispatched. Unconfirmed = response lost; it may have sold.';

/** The expired-rules blocker, by what the ticket is doing about it (#1223 renewal). */
const EXPIRED_RULES = {
  loading: 'This market’s rules have expired; renewing…',
  error: 'This market’s rules have expired; retrying automatically. Refresh to retry now.',
  idle: 'This market’s rules have expired. Refresh the market.',
};

/** A gate is still visible and still enforced. Its complete evidence belongs
 * to Details, rather than moving every field whenever evidence refreshes. */
function blockerSummary(reason: string, context: UsAccountContext): string {
  if (context.protocol === 'institutional') return context.readiness.state === 'unavailable'
    ? `${usReasonStatus(context.readiness.reason)} · trading not available yet.`
    : 'Institutional trading not available yet.';
  if (reason.includes('rules have expired')) return reason.includes('renewing')
    ? 'Refreshing market rules — orders paused.' : 'Market rules unavailable — orders paused.';
  if (reason.includes('current US book') || reason.includes('market list')) return 'Price unconfirmed — new orders unavailable.';
  if (reason.includes('does not name this outcome')) return 'Outcome unverified — new orders unavailable.';
  if (reason.startsWith('Trading is unavailable:')) return 'Trading unavailable for this account.';
  return reason;
}

/** True when a submit's result shows the venue has the order, with no lookup uncertainty. */
const venueHas = (result: UsTicketResult | null) => result !== null && result.lookupReason === null
  && (result.receipt?.status === 'submitted' || result.receipt?.status === 'accepted');

function targetFor(market: UsSelectedMarket, outcome: 'yes' | 'no'): UsTarget {
  const name = outcomeName(market, outcome);
  const title = market.display.question ?? marketTitle(market.market);
  return { marketSlug: market.market.marketSlug, outcome, label: `${title} — ${outcome.toUpperCase()}${name ? ` (${name})` : ''}` };
}

/** Whole cents ÷ contracts → cents per contract, for the odds beside Max Payout (K/P's all-in basis). */
function perContractCents(totalCents: bigint, quantity: string): number | null {
  const q = Number(quantity.trim());
  return Number.isFinite(q) && q > 0 ? Number(totalCents) / q : null;
}

const ODDS_SPLIT = <span className="h-3 w-px self-center bg-current opacity-30" aria-hidden />;

/**
 * K/P's summary card, on the same labels and shape (#1247): Price (¢ and
 * American odds) first, then Total Cost (fee), Max Payout with the all-in
 * odds beside it, and Max Profit; Proceeds (fee) for a sale. The odds come
 * from the same helper the K/P ticket uses. Values may wrap; they never widen
 * the ticket.
 */
function EconomicsRows({ economics, priceUsd, quantity, outcome }: {
  economics: UsOrderEconomics; priceUsd: string; quantity: string; outcome: string | null;
}) {
  const row = 'flex min-w-0 flex-wrap items-center justify-between gap-x-2';
  const value = 'min-w-0 text-right font-mono tabular-nums [overflow-wrap:anywhere]';
  const fee = <span className="text-muted-foreground" title={FEE_TITLE}>({formatUsdCents(economics.feeCents)})</span>;
  const priceCents = Number(usdToCentsText(priceUsd.trim()));
  const allIn = economics.action === 'buy' ? perContractCents(economics.totalCostCents, quantity) : null;
  return (
    <dl data-testid="us-ticket-economics" className="border-border mt-3 space-y-1 rounded-lg border px-3 py-2 text-sm">
      <div className={row} data-testid="us-ticket-price-row"
        title={`Limit price per ${outcome ?? 'outcome'} contract and its American odds; the fee is in Total Cost.`}>
        <dt className="text-muted-foreground">Price</dt>
        <dd className={cn(value, 'inline-flex items-center gap-1.5')}>
          <span>{usdToCentsText(priceUsd.trim())}¢</span>
          {ODDS_SPLIT}
          <span className="opacity-70" data-testid="us-ticket-price-odds">{americanOdds(priceCents)}</span>
        </dd>
      </div>
      {economics.action === 'buy' ? (
        <>
          <div className={row} data-testid="us-ticket-total-cost">
            <dt className="text-muted-foreground" title={FEE_TITLE}>Total Cost (fee)</dt>
            <dd className={value}>{formatUsdCents(economics.totalCostCents)} {fee}</dd>
          </div>
          <div className={row} data-testid="us-ticket-max-payout">
            <dt className="text-muted-foreground" title="Gross payout on win — $1 per contract">Max Payout</dt>
            <dd className={cn(value, 'text-positive inline-flex items-center gap-1.5')}>
              <span>{formatUsdCents(economics.maxPayoutCents)}</span>
              {ODDS_SPLIT}
              <span className="opacity-70" data-testid="us-ticket-payout-odds"
                title="American odds on the all-in cost per contract (price + estimated fee)">
                {allIn === null ? '—' : americanOdds(allIn)}
              </span>
            </dd>
          </div>
          <div className={row} data-testid="us-ticket-max-profit">
            <dt className="text-muted-foreground" title="Max Payout − Total Cost (net of the estimated fee)">Max Profit</dt>
            <dd className={cn(value, economics.maxProfitCents >= 0n ? 'text-positive' : 'text-negative')}>
              {formatUsdCents(economics.maxProfitCents, true)}
            </dd>
          </div>
        </>
      ) : (
        <div className={row} data-testid="us-ticket-proceeds">
          <dt className="text-muted-foreground" title={FEE_TITLE}>Proceeds (fee)</dt>
          <dd className={value}>{formatUsdCents(economics.proceedsCents)} {fee}</dd>
        </div>
      )}
    </dl>
  );
}

// Plain reasons in the text; each line keeps its raw code in its title.
const EDIT_STATUS = {
  refused: (code: string) => `Cancel not sent: ${usOrderReason(code)}. No replacement was placed. Check the original order before trying again.`,
  unconfirmed: (code: string) => `Cancel not confirmed: ${usOrderReason(code)}. No replacement was placed; check status first.`,
  filled: 'The original order filled before the cancel took effect. No replacement was placed.',
  filledDuringEdit: (n: string) => `${n} contracts filled during the edit, so the replacement was not placed.`
    + ' Review your position before placing another order.',
  canceled: (code: string | null) => `Original order canceled. Replacement not placed${code ? `: ${usOrderReason(code)}` : ''}. Nothing is resting.`,
  placing: 'Original order canceled. Placing the replacement…',
  replaced: (sent: boolean) => sent
    ? 'Original order canceled; the replacement was sent. Its receipt is below.'
    : 'Original order canceled. Replacement unconfirmed; it may be resting. Check Your saved US requests before placing another.',
};

/** An edit outcome the trader must act on (nothing placed, or unconfirmed): amber and announced. */
function editStatusWarns(phase: EditPhase): boolean {
  return phase.kind === 'canceled' || phase.kind === 'settlement' || (phase.kind === 'replaced' && !phase.sent);
}

/** The raw code behind an edit's status line, for its title. */
function editStatusCode(phase: EditPhase): string | undefined {
  if (phase.kind === 'canceled') return phase.code ?? undefined;
  if (phase.kind === 'settlement' && (phase.state === 'refused' || phase.state === 'unconfirmed')) return phase.code;
  return undefined;
}

function editStatus(phase: EditPhase): string | null {
  switch (phase.kind) {
    case 'ready': return null;
    case 'canceled': return EDIT_STATUS.canceled(phase.code);
    case 'placing': return EDIT_STATUS.placing;
    case 'replaced': return EDIT_STATUS.replaced(phase.sent);
    case 'settlement':
      switch (phase.state) {
        case 'refused': return EDIT_STATUS.refused(phase.code);
        case 'unconfirmed': return EDIT_STATUS.unconfirmed(phase.code);
        case 'filled': return EDIT_STATUS.filled;
        case 'filled_during_edit': return EDIT_STATUS.filledDuringEdit(phase.newlyFilled);
      }
  }
}

function TicketBody({
  context, onRequestRecorded, trading: tradingProp, markets: marketsProp, inspectInstitutionalDraft, clock = Date.now,
  lockedTarget, priceHintCents = null, priceHintSeq = null, hostMarketReadiness, layout = 'page', sizePresets, defaultExpiryMode, draftTerms, onDraftTermsChange, editOrder, cancel: cancelProp, onEditInterrupted, onEditStarted,
  closeHolding, onCloseSent, onEditReplaced, onCommittedChange, railControls, onSendInterrupted, onSendStarted, orderStatus: orderStatusProp,
}: BodyProps) {
  const { accountId, scope, permissions } = context;
  // Calls always reach the LATEST clients the composer passed, through stable
  // stand-ins. A composer that rebuilds its adapter wrapper on every render
  // (renderTicket cannot hold hooks) must not rebind the client — that would
  // drop an order being checked, or make the real client refuse a submit that
  // was already sent — nor reload the picker.
  const latest = useRef({ trading: tradingProp, markets: marketsProp, cancel: cancelProp, hostMarketReadiness, orderStatus: orderStatusProp });
  useLayoutEffect(() => { latest.current = { trading: tradingProp, markets: marketsProp, cancel: cancelProp, hostMarketReadiness, orderStatus: orderStatusProp }; });
  const following = !!orderStatusProp;
  function need(): UsTicketCancel {
    if (!latest.current.cancel) throw Object.assign(new Error('US_CLIENT_INVALID_REQUEST'), { code: 'US_CLIENT_INVALID_REQUEST' });
    return latest.current.cancel;
  }
  const [{ trading, markets, cancel }] = useState(() => ({
    trading: {
      setScope: (s) => latest.current.trading.setScope(s),
      setDraft: (d) => latest.current.trading.setDraft(d),
      preview: () => latest.current.trading.preview(),
      submit: () => latest.current.trading.submit(),
      acknowledge: (requestKey) => latest.current.trading.acknowledge?.(requestKey),
      canSubmit: () => latest.current.trading.canSubmit?.() ?? null,
    } satisfies UsTicketTrading,
    markets: {
      setSelection: (s) => latest.current.markets.setSelection(s),
      invalidate: () => latest.current.markets.invalidate(),
      listMarkets: (q) => latest.current.markets.listMarkets(q),
      readMarket: (slug) => latest.current.markets.readMarket(slug),
    } satisfies UsTicketMarkets,
    cancel: {
      setSelection: (s) => { if (s === null) latest.current.cancel?.setSelection(null); else need().setSelection(s); },
      cancelOrder: (r) => need().cancelOrder(r),
      checkOrderStatus: (r) => need().checkOrderStatus(r),
    } satisfies UsTicketCancel,
  }));
  const orderSection = useRef<HTMLElement>(null);
  const alive = useRef(0);
  const revisionCount = useRef(0);
  const marketRequest = useRef(0);
  /** Monotonic time of the last automatic rules renewal (or failed first read); see the renewal effect. */
  const lastAutoRefresh = useRef<number | null>(null);
  /** Bumped by anything that must drop an order still being checked. */
  const attempt = useRef(0);
  /** The synchronous twin of `stage`: a second click in the same tick sees it. */
  const stageRef = useRef<Stage>('idle');
  const draftSet = useRef(false);

  const lockedSlug = lockedTarget?.marketSlug ?? null;
  const [market, setMarket] = useState<MarketState>(() =>
    lockedSlug ? { status: 'loading', slug: lockedSlug, market: null } : { status: 'none' });
  // The rail (a host-bound order, not an edit or close) keeps a trader's terms through the host.
  const keepsTerms = !!railControls && !!lockedTarget && !editOrder && !closeHolding;
  const [entry, setEntry] = useState<UsTicketEntry>(() =>
    editOrder ? editEntryFrom(editOrder)
      : lockedTarget && closeHolding ? { ...EMPTY_ENTRY, outcome: lockedTarget.outcome, action: 'sell', quantity: closeHolding.quantity, postOnly: false }
        : lockedTarget ? { ...EMPTY_ENTRY, ...(keepsTerms && draftTerms ? draftTerms : {}), outcome: lockedTarget.outcome } : EMPTY_ENTRY);
  /** The entry as last rendered, plus edits made since: what an edit reports to the host. */
  const entryRef = useRef(entry);
  useLayoutEffect(() => { entryRef.current = entry; });
  const termsRef = useRef(onDraftTermsChange);
  useLayoutEffect(() => { termsRef.current = onDraftTermsChange; });
  /** Apply a trader-made change (or the rail's post-placement size) and tell the host the terms. */
  function applyEntry(patch: Partial<UsTicketEntry>) {
    setEntry((current) => ({ ...current, ...patch }));
    entryRef.current = { ...entryRef.current, ...patch };
    if (keepsTerms) termsRef.current?.(draftTermsOf(entryRef.current));
  }
  const [shown, setShown] = useState<Partial<Record<UsTicketField, boolean>>>({});
  const [stage, setStageState] = useState<Stage>('idle');
  const [failure, setFailure] = useState<Failure | null>(null);
  /** The last submission, with the market's own outcome name and question as shown when it was sent. */
  const [last, setLast] = useState<{ result: UsTicketResult; draft: UsDraft; outcomeName: string | null; question: string | null } | null>(null);
  /** The latest read of the last submission's intent (#1247); kept by intent, so a new submission starts clean. */
  const [followed, setFollowed] = useState<{ intentId: string; row: UsOrderRow } | null>(null);
  const [now, setNow] = useState(clock);
  const [editPhase, setEditPhase] = useState<EditPhase>({ kind: 'ready' });
  /** Set once an edit's cancel may have gone out, until the edit's outcome is known and shown. */
  const editOpen = useRef(false);
  /** Set from a confirmed cancel until the replacement's outcome is known:
   * its re-check (stage `checking`) is as committed as its send. */
  const placingRef = useRef(false);
  /** From the cancel on, the entry, market and client draft stay as checked. */
  const committed = stage === 'canceling' || stage === 'sending' || editPhase.kind === 'placing';
  /** A cancel or submit is out: the client's draft must be left alone. */
  const inFlight = () => stageRef.current === 'canceling' || stageRef.current === 'sending';
  const isCommitted = () => inFlight() || placingRef.current;
  const committedRef = useRef(onCommittedChange);
  useLayoutEffect(() => { committedRef.current = onCommittedChange; });
  useEffect(() => { committedRef.current?.(committed); }, [committed]);
  useEffect(() => () => committedRef.current?.(false), []);
  // An edit can be (re)started only while the original is known untouched.
  const editable = editPhase.kind === 'ready' || (editPhase.kind === 'settlement' && editPhase.state === 'refused');

  function setStage(next: Stage) {
    stageRef.current = next;
    // Lock the host before calling cancel/submit, while its old Escape/X
    // handlers may still be live. Unlock stays in the effect above so the
    // settled outcome is rendered before dismissal becomes available again.
    if (next === 'canceling' || next === 'sending' || placingRef.current) committedRef.current?.(true);
    setStageState(next);
  }

  // Leaving while an edit's outcome is open (an account or scope change
  // remounts this body) must not silently drop what its cancel did.
  const interruptedRef = useRef(onEditInterrupted);
  useLayoutEffect(() => { interruptedRef.current = onEditInterrupted; });
  // A plain order's submit still out when the body goes: its receipt will not
  // be shown here, so say so above the new body and have the host re-read the
  // saved request (the client froze it before sending).
  const sendInterruptedRef = useRef({ onSendInterrupted, onRequestRecorded });
  useLayoutEffect(() => { sendInterruptedRef.current = { onSendInterrupted, onRequestRecorded }; });
  useEffect(() => () => {
    if (editOpen.current) interruptedRef.current();
    else if (stageRef.current === 'sending') {
      sendInterruptedRef.current.onSendInterrupted();
      sendInterruptedRef.current.onRequestRecorded();
    }
  }, []);

  // Bind this ticket's own clients to the account; release them on unmount.
  // A layout effect, so the binding lands before the picker's first read.
  // Keyed by value: the body is already keyed by account and scope, so a
  // parent re-render with an equal new object must not rebind (and drop) the draft.
  const { venue, scopeKey, revision: scopeRevision } = scope;
  useLayoutEffect(() => {
    const mine = ++alive.current;
    // A refused binding is not swallowed: the clients then refuse every read
    // and draft, and the picker and the Place Order result show that code.
    try { markets.setSelection({ accountId, scope: { venue, scopeKey, revision: scopeRevision } }); } catch { /* shown by the picker */ }
    try { trading.setScope({ accountId, scopeKey, revision: scopeRevision }); } catch { /* shown on Place Order */ }
    const guards = { alive, attempt, marketRequest };
    return () => {
      guards.alive.current = mine + 1;
      guards.attempt.current += 1;
      guards.marketRequest.current += 1;
      trading.setScope(null);
      markets.invalidate();
      // Drops a cancel or status read still out: its late answer can't paint here.
      cancel.setSelection(null);
      try { latest.current.orderStatus?.setSelection(null); } catch { /* releasing cannot usefully fail */ }
    };
  }, [accountId, venue, scopeKey, scopeRevision, markets, trading, cancel]);

  // A host-bound target reads its market once the clients are bound (the
  // layout effect above runs first). A new target remounts the body.
  useEffect(() => {
    if (!lockedSlug) return;
    const mine = ++marketRequest.current;
    markets.readMarket(lockedSlug).then((next) => {
      if (mine !== marketRequest.current) return;
      setNow(clock());
      setMarket({ status: 'ready', slug: lockedSlug, market: next });
    }, (e: unknown) => {
      if (mine !== marketRequest.current) return;
      // The failure stays on screen; the renewal below retries it on its 10 s gate.
      lastAutoRefresh.current = performance.now();
      setMarket({ status: 'error', slug: lockedSlug, market: null, reason: reasonOf(e, 'US_MARKET_UNAVAILABLE') });
    });
  }, [lockedSlug, markets, clock]);

  // #1247: the receipt follows its order. After a submit that left an intent
  // which can still change, the ticket reads that exact intent back (saved
  // evidence through a dedicated portfolio client; never a cancel, never a
  // resend), one read at a time, at 1, 2, 4, 8, 15 and then every 30 s while
  // the page is visible, until the order is filled, canceled, expired or
  // rejected. A failed read keeps the last answer on screen.
  const followIntent = last?.result.receipt && last.result.receipt.status !== 'aborted' && last.result.receipt.status !== 'rejected'
    ? last.result.receipt.intentId : null;
  useEffect(() => {
    if (!followIntent || !following) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let step = 0;
    const schedule = () => { timer = setTimeout(() => void read(), FOLLOW_DELAYS_MS[Math.min(step++, FOLLOW_DELAYS_MS.length - 1)]); };
    const read = async () => {
      const client = latest.current.orderStatus;
      if (stopped || !client) return;
      if (document.visibilityState === 'hidden') { schedule(); return; }
      try {
        client.setSelection({ accountId, scopeKey, revision: scopeRevision, view: 'own' });
        const row = await client.checkOrderStatus({ scope: { venue, scopeKey, revision: scopeRevision }, intentId: followIntent });
        if (stopped) return;
        if (row.intentId !== followIntent) { schedule(); return; }
        setFollowed({ intentId: followIntent, row });
        const done = row.lifecycle ? US_TERMINAL_LIFECYCLE.includes(row.lifecycle.state)
          : row.receipt.status === 'aborted' || row.receipt.status === 'rejected';
        if (done) return;
      } catch {
        if (stopped) return;
      }
      schedule();
    };
    schedule();
    return () => { stopped = true; clearTimeout(timer); };
  }, [followIntent, following, accountId, venue, scopeKey, scopeRevision]);
  const followedRow = followed && followed.intentId === followIntent ? followed.row : null;

  /** Clear the client's draft, never while a submit is in flight. */
  function releaseDraft() {
    if (!draftSet.current || inFlight()) return;
    draftSet.current = false;
    try { trading.setDraft(null); } catch { /* releasing a draft cannot usefully fail */ }
  }

  /**
   * The order changed under a running check: drop it before it can be sent.
   * A submit already called is left alone; its own result is shown when it
   * settles. `clearFailure` (an edit) also clears the last click's message.
   */
  function supersede(clearFailure: boolean) {
    if (isCommitted()) return;
    attempt.current += 1;
    const wasChecking = stageRef.current === 'checking';
    if (wasChecking) setStage('idle');
    releaseDraft();
    if (wasChecking) {
      setFailure({ kind: 'superseded' });
      playUsRejectedTone();
    }
    else if (clearFailure) setFailure(null);
  }

  function edit(patch: Partial<UsTicketEntry>) {
    if (isCommitted() || (editOrder && !editable)) return;
    applyEntry(patch);
    setShown((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as (keyof UsTicketEntry)[]) next[FIELD_OF[key]] = true;
      return next;
    });
    supersede(true);
  }

  // A host price pick is a command, like the K/P ticket's prefill: it writes
  // the price once per new pick, like any edit. A pick is new by its sequence
  // as well as its cents, so the same level picked again after a manual edit
  // is applied again (and the box flashes, as the K/P ticket's does).
  const hintUsd = priceHintCents === null ? null : centsToUsd(String(priceHintCents));
  const hintKey = hintUsd === null ? null : `${priceHintSeq ?? ''}|${hintUsd}`;
  const appliedHint = useRef<string | null>(null);
  const [priceFlashing, setPriceFlashing] = useState(false);
  useEffect(() => {
    if (hintUsd === null || hintKey === null || appliedHint.current === hintKey) return;
    appliedHint.current = hintKey;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- an external price command, not derived state
    edit({ outcomePrice: hintUsd });
    setPriceFlashing(true);
    const t = setTimeout(() => setPriceFlashing(false), 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new pick should write the price
  }, [hintKey]);

  /** The trader's default size, floored at this market's minimum, as the rail seeds it. */
  const railDefaultQuantity = (rules: UsSelectedMarket['rules']) =>
    String(Math.max(sizePresets?.defaultContracts ?? 1, Number(rules.minimumQuantity)));
  const railPreset = useRef(false);
  useLayoutEffect(() => {
    if (layout !== 'rail' || !railControls || !lockedTarget || editOrder || closeHolding
      || market.status !== 'ready' || railPreset.current) return;
    railPreset.current = true;
    const { rules, timeEvidence } = market.market;
    const evidenceNow = usEvidenceNow(timeEvidence, clock());
    const book = market.market.market.book;
    const bid = lockedTarget.outcome === 'yes' ? book.yesBidUsd : book.noBidUsd;
    const freshBid = book.state === 'current' && book.expiresAtMs !== null && book.expiresAtMs > evidenceNow ? bid : null;
    // Only initial empty fields are seeded; a refresh never rewrites an entry.
    // The side the account permits: a sell-only trader starts on Sell, not a refused Buy.
    const side = permissions.buy ? 'buy' : permissions.sell ? 'sell' : null;
    // The duration follows the trader's Polymarket US preference, as the K/P
    // tickets seed theirs. A Good to Start with no known future start (or no
    // dated duration here) presets nothing and the row says why: a missing or
    // past start must never silently become an unbounded Good to Close.
    const duration = (): Pick<UsTicketEntry, 'timeInForce' | 'expiry'> | null => {
      if (defaultExpiryMode === 'gtc' && rules.timeInForce.includes('TIME_IN_FORCE_GOOD_TILL_CANCEL')) {
        return { timeInForce: 'TIME_IN_FORCE_GOOD_TILL_CANCEL', expiry: '' };
      }
      if (defaultExpiryMode === 'gts' && gtsAllowedFor(railControls.startTimeMs, evidenceNow, rules)) {
        return { timeInForce: GTD, expiry: startExpiryText(railControls.startTimeMs) };
      }
      if (defaultExpiryMode === 'custom' && rules.timeInForce.includes(GTD)) return { timeInForce: GTD, expiry: '' };
      return null;
    };
    setEntry(current => ({ ...current, action: current.action ?? side,
      quantity: current.quantity || railDefaultQuantity(rules),
      outcomePrice: current.outcomePrice || freshBid || '',
      ...(current.timeInForce === null ? duration() ?? {} : {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time host preset after authoritative rules arrive
  }, [market, layout, railControls]);

  // #1006 close: once this market's rules are known, preset the marketable
  // sell (immediate duration at the lowest price the ticket accepts on the
  // market's grid), once, like any edit.
  const closeRules = closeHolding && market.status === 'ready' ? market.market.rules : null;
  const closePreset = useRef(false);
  /** What the market lets this close sell, from the rules on screen: the last
   * market read stays on screen through a refresh (or a failed one), and so
   * do the Closing, remainder and too-small lines read from it. */
  const shownCloseRules = closeHolding && market.status !== 'none' ? market.market?.rules ?? null : null;
  const closeFit = closeHolding && shownCloseRules
    ? closeQuantityFor(closeHolding.quantity, shownCloseRules.quantityStep, shownCloseRules.minimumQuantity) : null;
  // A layout effect, so the preset size is in the box before the first paint
  // with the rules: no frame says "sell all 12.5" over a box about to hold 12.
  useLayoutEffect(() => {
    if (!closeRules || !closeHolding || closePreset.current) return;
    closePreset.current = true;
    const immediate = closeImmediateDuration(closeRules.timeInForce);
    const floor = lockedTarget ? lowestValidPrice(closeRules.priceTick, lockedTarget.outcome) : null;
    const fit = closeQuantityFor(closeHolding.quantity, closeRules.quantityStep, closeRules.minimumQuantity);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- a one-time preset command, not derived state
    edit({ ...(floor ? { outcomePrice: floor } : {}), ...(immediate ? { timeInForce: immediate } : {}), postOnly: false,
      ...(fit?.sell ? { quantity: fit.sell } : {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the preset is written once, when the rules arrive
  }, [closeRules]);

  // The account snapshot, compared by value: an equal one re-sent by a busy
  // parent is not a change. `accessNow` is read when a check answers, so a
  // change that lands before the effect below still drops the attempt.
  const access = JSON.stringify([context.accountEnabled, context.scopeUse, context.permissions, context.readiness]);
  const accessNow = useRef(access);
  useLayoutEffect(() => { accessNow.current = access; }, [access]);
  const lastAccess = useRef(access);
  useEffect(() => {
    if (lastAccess.current === access) return;
    lastAccess.current = access;
    supersede(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a changed access snapshot should drop an attempt
  }, [access]);

  // Rules freshness is a clock fact; re-read the clock each second.
  const ticking = market.status !== 'none';
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setNow(clock()), 1_000);
    return () => clearInterval(id);
  }, [ticking, clock]);

  async function loadMarket(slug: string, keepEntry: boolean) {
    if (isCommitted()) return;
    const mine = ++marketRequest.current;
    const previous = market.status !== 'none' && market.slug === slug ? market.market : null;
    setMarket({ status: 'loading', slug, market: previous });
    if (!keepEntry) {
      // A new target: outcome, price and duration belong to the old market.
      setEntry((current) => ({ ...EMPTY_ENTRY, action: current.action, quantity: current.quantity }));
      setShown({});
      supersede(true);
    }
    try {
      const next = await markets.readMarket(slug);
      if (mine !== marketRequest.current) return;
      setNow(clock());
      setMarket({ status: 'ready', slug, market: next });
      // A new rules revision changes what a running check was made against.
      if (keepEntry && previous && previous.rules.revision !== next.rules.revision) supersede(false);
    } catch (e) {
      if (mine !== marketRequest.current) return;
      setMarket({ status: 'error', slug, market: previous, reason: reasonOf(e, 'US_MARKET_UNAVAILABLE') });
      if (previous) supersede(false);
    }
  }

  // During a refresh (or after one fails) fields remain mounted and editable
  // so focus and the draft survive. Only fresh 'ready' evidence can be placed.
  // #1223: the open ticket owns short-lived venue rules. Renew expired
  // evidence, not its timestamp, while idle and visible. One request at a
  // time; failures stay visible and retry at most every ten seconds. A
  // host-bound target whose first read failed (no rules yet) is renewed on
  // the same gate: nothing on screen can be placed until it answers.
  useEffect(() => {
    if (document.visibilityState === 'hidden' || isCommitted() || stageRef.current !== 'idle'
      || market.status === 'none' || market.status === 'loading') return;
    if (market.market) {
      if (market.market.rules.expiresAtMs > usEvidenceNow(market.market.timeEvidence, now)) return;
    } else if (market.status !== 'error' || !lockedSlug) return;
    const retryNow = performance.now();
    if (lastAutoRefresh.current !== null && retryNow - lastAutoRefresh.current < 10_000) return;
    lastAutoRefresh.current = retryNow;
    void loadMarket(market.slug, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clock/selection trigger; current attempt guards are read synchronously
  }, [now, market, stage]);

  const selected = market.status === 'none' ? null : market.market;
  const marketReady = market.status === 'ready';
  const rulesExpired = selected !== null && selected.rules.expiresAtMs <= usEvidenceNow(selected.timeEvidence, now);
  const hostBlocker = hostMarketBlocker(hostMarketReadiness, now);
  const expectedLabel = lockedTarget?.nativeOutcomeLabel;
  const labelBlocker = selected && expectedLabel !== undefined && lockedTarget
    && outcomeName(selected, lockedTarget.outcome) !== expectedLabel
    ? `Polymarket US does not name this outcome "${expectedLabel}". New orders are disabled.` : null;
  const marketBlockers = [
    ...(hostBlocker ? [hostBlocker] : []),
    ...(labelBlocker ? [labelBlocker] : []),
    ...(selected && !selected.rules.active ? ['This market is not open for new orders.'] : []),
    ...(rulesExpired ? [EXPIRED_RULES[market.status === 'loading' ? 'loading' : market.status === 'error' ? 'error' : 'idle']] : []),
  ];
  const check = selected ? checkEntry(entry, selected.rules, context, usEvidenceNow(selected.timeEvidence, now)) : null;
  const editBlocked = editOrder && editable ? editBlocker(editOrder, context, now) : null;
  const blockers = [...(check?.blockers ?? accountBlockers(context)), ...marketBlockers, ...(editBlocked ? [editBlocked] : [])];
  // The host's market reason (no current book, a market list it cannot vouch
  // for) sits beside the order action it disables, where the trader is
  // looking; the list above carries it only while that action is not shown.
  const actionShown = selected !== null && check !== null;
  const listedBlockers = actionShown && hostBlocker ? blockers.filter((b) => b !== hostBlocker) : blockers;
  const railBlockers = [...new Set([...blockers,
    ...(market.status === 'error' ? [`Market unavailable: ${usOrderReason(market.reason)}. Your entries are kept.`] : [])])];
  const clean = marketReady && check !== null && isClean(check) && marketBlockers.length === 0;
  /** Reasons revealing field errors would not help; the list above already names them. */
  const blocked = !marketReady || blockers.length > 0;
  const institutional = context.protocol === 'institutional';
  const economics = !institutional && check && !check.fields.quantity && !check.fields.price ? estimateUsOrderEconomics(entry) : null;

  function focusFirstInvalid(fields: Partial<Record<UsTicketField, string>>) {
    const first = ALL_FIELDS.find((f) => fields[f]);
    const root = orderSection.current;
    if (!first || !root) return;
    if (first === 'duration' && railControls && !editOrder && !closeHolding) {
      root.querySelector<HTMLButtonElement>('[data-us-duration] button:not(:disabled)')?.focus();
      return;
    }
    const control = root.querySelector<HTMLElement>(`[aria-labelledby="us-ticket-${first}-label"]`);
    const target = control?.getAttribute('role') === 'radiogroup'
      ? control.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')
      : control ?? root.querySelector<HTMLElement>(`[data-field="${first}"] input`);
    // A field folded under "More order options" is opened first, so the focus lands somewhere visible.
    const folded = target?.closest('details');
    if (folded) folded.open = true;
    target?.focus();
  }

  /** Stop at the check step: nothing was sent, and the entry stays. */
  function notSent(code: string) {
    setStage('idle');
    releaseDraft();
    setFailure({ kind: 'not-sent', code });
    playUsRejectedTone();
  }

  /** A fresh draft revision of the current entry for `selected`. */
  function draftOf(from: UsTicketEntry): UsDraft {
    revisionCount.current += 1;
    return buildDraft(from, scope, targetFor(selected!, from.outcome!), `draft-${revisionCount.current}`);
  }

  /**
   * Step 1: bind the draft and have the relay check it. Nothing is sent. Null
   * when the attempt stopped; it has already said why.
   */
  async function checkDraft(draft: UsDraft): Promise<UsPreview | null> {
    const mine = ++attempt.current;
    const accessAtClick = access;
    setFailure(null);
    // A prior success cannot appear as the answer to this new attempt.
    // Saved uncertain requests remain in recovery; nothing is acknowledged here.
    setLast(null);
    onSendStarted();
    setStage('checking');
    try {
      trading.setDraft(draft);
      draftSet.current = true;
    } catch (e) {
      notSent(reasonOf(e, 'US_CLIENT_INVALID_SCOPE'));
      return null;
    }
    let answer;
    try {
      answer = await trading.preview();
    } catch (e) {
      if (mine === attempt.current) notSent(reasonOf(e, 'US_PREVIEW_UNAVAILABLE'));
      return null;
    }
    if (mine !== attempt.current) return null; // an edit or account change dropped it, and said so
    if (accessNow.current !== accessAtClick) {
      // A replacement's re-check cannot be superseded; it stops here unsent.
      if (placingRef.current) notSent('US_ACCESS_CHANGED');
      else supersede(false);
      return null;
    }
    if (hostMarketBlocker(latest.current.hostMarketReadiness, clock())) {
      notSent('US_MARKET_UNAVAILABLE');
      return null;
    }
    if (answer.draftRevision !== draft.draftRevision || answer.scopeKey !== scope.scopeKey) {
      notSent('US_PREVIEW_MISMATCH');
      return null;
    }
    return answer;
  }

  /**
   * Step 2: send once. From here the client's draft is left alone until it
   * settles. Returns the refusal code when the client proved nothing was sent.
   */
  /** The last submit's result, read by a close right after `send` returns. */
  const lastSent = useRef<UsTicketResult | null>(null);
  async function send(draft: UsDraft): Promise<{ notSent: string } | null> {
    lastSent.current = null;
    if (hostMarketBlocker(latest.current.hostMarketReadiness, clock())) {
      notSent('US_MARKET_UNAVAILABLE');
      return { notSent: 'US_MARKET_UNAVAILABLE' };
    }
    setStage('sending');
    const life = alive.current;
    try {
      const result = await trading.submit();
      lastSent.current = result;
      if (life !== alive.current) return null;
      setLast({ result, draft, outcomeName: selected ? outcomeName(selected, draft.target.outcome) : null,
        question: selected ? selected.display.question ?? marketTitle(selected.market) : null });
      // A settled receipt is on screen now: the saved-request reminder is no
      // longer needed. An unconfirmed result keeps it, since its lines send
      // the trader there. A failure here only leaves the reminder in place.
      if (result.receipt && result.lookupReason === null && result.receipt.status !== 'unknown') {
        try { trading.acknowledge?.(result.requestKey); } catch { /* the reminder stays */ }
      }
      // By ear as on Kalshi: placed when Polymarket US has it, the descent
      // when nothing was placed, nothing while the result is unconfirmed.
      if (venueHas(result)) playUsPlacedTone();
      // As the K/P ticket clears its size once the venue has the order: the
      // rail goes back to the default size; the price and terms stay.
      if (venueHas(result) && keepsTerms && selected) applyEntry({ quantity: railDefaultQuantity(selected.rules) });
      else if (result.receipt?.status === 'aborted' || result.receipt?.status === 'rejected') playUsRejectedTone();
      return null;
    } catch (e) {
      if (life !== alive.current) return null;
      const code = reasonOf(e, 'US_SUBMIT_UNAVAILABLE');
      const notSent = NOT_SENT.has(code);
      setFailure({ kind: notSent ? 'not-sent' : 'unconfirmed', code });
      if (notSent) playUsRejectedTone();
      return notSent ? { notSent: code } : null;
    } finally {
      if (life === alive.current) {
        setStage('idle');
        onRequestRecorded();
      }
    }
  }

  /** Incomplete entry: show every problem and go to the first. Nothing is sent. */
  function revealProblems(fields: Partial<Record<UsTicketField, string>>) {
    setShown(Object.fromEntries(ALL_FIELDS.map((f) => [f, true])));
    focusFirstInvalid(fields);
  }

  /** #1006: a close sends once. Set before the send; cleared only when the
   * send provably went nowhere, so "Place Order" can never sell twice. */
  const closeSentRef = useRef(false);
  const [closeOutcome, setCloseOutcome] = useState<CloseOutcome | null>(null);
  async function placeOrder() {
    if (stageRef.current !== 'idle' || !selected || !check || blocked) return;
    if (closeHolding && closeSentRef.current) return;
    setLast(null);
    if (!clean) return revealProblems(check.fields);
    const draft = draftOf(entry);
    if (!(await checkDraft(draft))) return;
    if (!closeHolding) {
      await send(draft);
      return;
    }
    // Guard the holding before anything can leave; undo only on proof.
    closeSentRef.current = true;
    onCloseSent?.({ phase: 'sending' });
    const refused = await send(draft);
    const result = lastSent.current;
    const receipt = result?.receipt ?? null;
    if (refused || receipt?.status === 'aborted' || receipt?.status === 'rejected') {
      // Nothing was placed (a client refusal, a proven-unsent receipt, or the
      // venue's rejection): the trader may close again from this window.
      closeSentRef.current = false;
      onCloseSent?.({ phase: 'not-sent' });
      return;
    }
    // The line says what the receipt says. Recorded-but-not-sent and
    // unconfirmed keep the guard: this window never sells a second time.
    setCloseOutcome(venueHas(result) ? 'sent'
      : receipt?.status === 'ready' && result?.lookupReason === null ? 'recorded' : 'unconfirmed');
    onCloseSent?.({ phase: 'sent', intentId: receipt?.intentId ?? null });
  }

  // ── Edit (#1017): check, cancel, confirm, then place ──────────────────────

  /** The checked draft an edit will place; kept across an unconfirmed cancel. */
  const editDraft = useRef<UsDraft | null>(null);
  /**
   * The order as it stood at the Replace click. Every later judgment (fills
   * during the edit, same order) is made against it: a host refreshing the
   * `editOrder` row mid-edit must not move the baseline.
   */
  const editOriginal = useRef<UsOrderRow | null>(null);
  const unchanged = editOrder ? editUnchanged(entry, editOrder) : false;
  const unknownTerms = editOrder ? editUnknownTerms(editOrder) : null;
  const originalTerms = editOrder ? editTermsHeader(editOrder) : '';

  async function replaceOrder() {
    if (!editOrder || !editable || stageRef.current !== 'idle' || !selected || !check || blocked || unchanged) return;
    if (!clean) return revealProblems(check.fields);
    const draft = draftOf(entry);
    const accessAtClick = access;
    const original = editOrder;
    const preview = await checkDraft(draft);
    if (!preview) return;
    // Check before destroying: a refusal this browser can predict (its saved-
    // request queue, its storage, its session) stops the edit before the cancel.
    const unsaveable = trading.canSubmit();
    if (unsaveable) {
      setStage('idle');
      releaseDraft();
      setFailure({ kind: 'edit-not-started', code: unsaveable });
      playUsRejectedTone();
      return;
    }

    // The cancel. From here the entry is frozen and the edit's outcome is open.
    editDraft.current = draft;
    editOriginal.current = original;
    editOpen.current = true;
    onEditStarted();
    setStage('canceling');
    const life = alive.current;
    let settlement: UsEditSettlement;
    try {
      cancel.setSelection({ accountId, scopeKey, revision: scopeRevision, view: original.ownership });
      const result = await cancel.cancelOrder({ scope: original.scope, intentId: original.intentId, orderId: original.orderId! });
      settlement = editCancelSettlement(result, original);
    } catch (e) {
      settlement = editCancelFailure(reasonOf(e, 'US_CANCEL_UNCONFIRMED'));
    }
    if (life !== alive.current) return;
    if (settlement.state !== 'settled') return settle(settlement);
    if (accessNow.current !== accessAtClick) return settle({ state: 'settled' }, 'US_ACCESS_CHANGED');
    // A check outlived by the cancel is redone for the same terms.
    const fresh = Date.parse(preview.expiresAt) > usEvidenceNow(preview.timeEvidence, clock());
    await placeReplacement(fresh ? draft : null);
  }

  /** The outcome of a placement that ended without anything resting. */
  function placementStopped(code: string | null) {
    if (stageRef.current === 'checking') setStage('idle');
    releaseDraft();
    setEditPhase({ kind: 'canceled', code });
  }

  /** Show where the edit stands once the cancel (or a status check) answered. */
  function settle(settlement: UsEditSettlement, notPlacedCode: string | null = null) {
    setStage('idle');
    if (settlement.state === 'settled') {
      releaseDraft();
      setEditPhase({ kind: 'canceled', code: notPlacedCode });
      // A guard held the replacement back (the descent); a plain confirmed
      // cancel only offers Place replacement.
      if (notPlacedCode) playUsRejectedTone();
      return;
    }
    // A refused cancel placed nothing: the descent. A fill that ended the edit
    // traded (the fill channel speaks for it) and an unconfirmed cancel is not
    // "not placed": both play nothing, as does every status read, which can
    // only answer settled, filled or unconfirmed.
    if (settlement.state === 'refused') playUsRejectedTone();
    // Refused: nothing was sent. Filled: the outcome is known and shown.
    // Unconfirmed stays open; a later placement re-checks the kept terms.
    if (settlement.state !== 'unconfirmed') editOpen.current = false;
    if (settlement.state === 'refused') editDraft.current = null;
    releaseDraft();
    setEditPhase({ kind: 'settlement', ...settlement });
  }

  /**
   * Send the edit's checked draft once the original is confirmed gone. With
   * no still-valid check (`checked` null), the same terms are checked again
   * under a new revision first; that check sends nothing.
   */
  async function placeReplacement(checked: UsDraft | null) {
    const kept = editDraft.current;
    if (!kept || placingRef.current) return;
    // Committed from here (not only while sending): typing, Refresh market,
    // Replace order and the host's dismiss all wait for the outcome.
    placingRef.current = true;
    committedRef.current?.(true);
    setEditPhase({ kind: 'placing' });
    try {
      let draft = checked;
      if (!draft) {
        revisionCount.current += 1;
        draft = { ...kept, draftRevision: `draft-${revisionCount.current}` };
        if (!(await checkDraft(draft))) return placementStopped(null);
      }
      const refused = await send(draft);
      // Proven unsent: the original is gone and nothing replaced it, so the
      // placement stays on offer. Otherwise the receipt (or its uncertainty) shows.
      if (refused) return placementStopped(refused.notSent);
      const result = lastSent.current;
      const receipt = result?.receipt ?? null;
      // Proven unsent (aborted) or refused by the venue (rejected): nothing rests.
      if (receipt?.status === 'aborted' || receipt?.status === 'rejected') return placementStopped(receipt.reason);
      setEditPhase({ kind: 'replaced', sent: venueHas(result) });
      editOpen.current = false;
      if (receipt) onEditReplaced?.(receipt.intentId);
    } finally {
      placingRef.current = false;
    }
  }

  /** "Check status" after an unconfirmed cancel: a read, never another cancel. */
  async function checkEditStatus() {
    const original = editOriginal.current;
    if (!original || stageRef.current !== 'idle') return;
    setStage('canceling');
    const life = alive.current;
    let settlement: UsEditSettlement;
    try {
      // Re-bound to the current session first: a login change since the
      // cancel would otherwise refuse every read. A read never cancels.
      cancel.setSelection({ accountId, scopeKey, revision: scopeRevision, view: original.ownership });
      settlement = editOrderSettlement(await cancel.checkOrderStatus({ scope: original.scope, intentId: original.intentId }), original);
    } catch (e) {
      settlement = { state: 'unconfirmed', code: reasonOf(e, 'US_READ_UNAVAILABLE') };
    }
    if (life !== alive.current) return;
    settle(settlement);
  }

  return (
    <section aria-label="Polymarket US order ticket" data-testid="us-ticket" className={cn("min-w-0", !keepsTerms && 'mb-3', layout === 'page' && "border-border rounded border p-3")}>
      {keepsTerms ? (<>
        {/* #1247: the Game Detail rail's heading in the K/P shape — Order Entry,
            the account chip, and the contract id (the venue slug) at the right,
            with Refresh market as a small control beside it. */}
        <header className="flex min-w-0 items-center justify-between gap-2" data-testid="us-ticket-heading">
          <span className="flex min-w-0 items-center gap-1.5">
            <h2 className="text-muted-foreground shrink-0 text-xs font-medium">Order Entry</h2>
            {railControls?.accountChip}
          </span>
          <span className="flex min-w-0 items-center gap-1">
            <span title={lockedTarget!.marketSlug} data-testid="us-ticket-contract"
              className="text-muted-foreground/50 min-w-0 truncate font-mono text-[10px]">
              {lockedTarget!.marketSlug.length > 30 ? `...${lockedTarget!.marketSlug.slice(-28)}` : lockedTarget!.marketSlug}
            </span>
            {market.status !== 'none' && (
              <button type="button" title={HELP.refreshMarket} data-testid="us-ticket-refresh"
                className="text-muted-foreground/70 hover:text-foreground flex shrink-0 items-center rounded p-0.5 disabled:opacity-50"
                disabled={market.status === 'loading' || committed} onClick={() => void loadMarket(market.slug, true)}>
                <RefreshCw size={10} aria-hidden className={cn(market.status === 'loading' && 'animate-spin')} />
                <span className="sr-only">{market.status === 'loading' ? 'Refreshing…' : 'Refresh market'}</span>
              </button>
            )}
          </span>
        </header>
        {/* Said in the open only when a side is not permitted. */}
        <p className={cn("text-muted-foreground text-xs", permissions.buy && permissions.sell && 'sr-only')} data-testid="us-ticket-permissions">
          Buy {permissions.buy ? 'allowed' : 'not permitted'} · Sell {permissions.sell ? 'allowed' : 'not permitted'}
        </p>
        <div className="min-h-4" aria-label="Order availability">
          {railBlockers.length > 0 ? <UsTicketNotice summary={blockerSummary(railBlockers[0], context)} testId="us-ticket-blockers"
            title={market.status === 'error' ? market.reason : undefined}>
            <ul aria-label="Why orders are unavailable">{railBlockers.map(b => <li key={b}>{b}</li>)}</ul>
          </UsTicketNotice> : market.status === 'loading' ? <p role="status" className="text-[11px] leading-4 text-muted-foreground">Loading market rules…</p> : null}
        </div>
        </>
      ) : (
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {layout === 'page' && <PortfolioVenueBadge venue="polymarket_us" />}
        <h2 className={layout === 'rail' ? "text-muted-foreground text-xs font-medium" : "text-sm font-semibold"}>{editOrder ? 'Polymarket US — edit order' : closeHolding ? 'Polymarket US — close holding' : layout === 'rail' ? 'Order Entry' : 'Polymarket US — manual limit order'}</h2>
        {layout === 'rail' && !editOrder && market.status !== 'none' && <button type="button"
          className="ml-auto text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          disabled={market.status === 'loading' || committed} onClick={() => void loadMarket(market.slug, true)}>
          {market.status === 'loading' ? 'Refreshing…' : 'Refresh market'}
        </button>}
        {layout === 'page' && <span className="text-muted-foreground min-w-0 break-all text-xs">Account {accountId}</span>}
        <span className={cn("text-muted-foreground text-xs", layout === 'rail' && permissions.buy && permissions.sell && 'sr-only')} data-testid="us-ticket-permissions">
          Buy {permissions.buy ? 'allowed' : 'not permitted'} · Sell {permissions.sell ? 'allowed' : 'not permitted'}
        </span>
      </header>
      )}
      {editOrder && (
        <p className="text-muted-foreground mt-1 break-words text-xs [overflow-wrap:anywhere]" data-testid="us-ticket-edit-order">
          Editing {editOrder.action.toUpperCase()} {editOrder.target.outcome.toUpperCase()} · {editEntryFrom(editOrder).quantity} left
          at ${editOrder.outcomePrice}{originalTerms && ` · ${originalTerms}`}{layout === 'page' && ` · Intent ${editOrder.intentId}`}
        </p>
      )}
      {unknownTerms && (
        <p className="mt-1 break-words text-xs text-amber-500 light:text-amber-700" data-testid="us-ticket-edit-terms-unknown">
          {unknownTerms}
        </p>
      )}

      {closeHolding && lockedTarget && (
        <UsTicketNotice warning={false} className="mt-1" testId="us-ticket-close-holding"
          summary={`${closeFit?.sell ? 'Closing' : 'Holding'} ${closeFit?.sell ? closeSellAmount(entry.quantity, closeHolding.quantity) : closeHolding.quantity} ${lockedTarget.outcome.toUpperCase()}`}>
        <p>
          {/* An amount only once the market's rules say this holding can be
              sold; before that (or when it can't be read, or is too small)
              the line names the holding, not a sale. */}
          {closeFit?.sell
            ? <>Closing: sell {closeSellAmount(entry.quantity, closeHolding.quantity)} {lockedTarget.outcome.toUpperCase()} at market.</>
            : <>Closing your {closeHolding.quantity} {lockedTarget.outcome.toUpperCase()} at market.</>}
          {' '}{closeDurationNote(entry.timeInForce)}
          {' '}Proceeds below are the minimum, at the price shown; fills at higher bids pay more. Raise the price to sell
          only at or above it. Nothing is retried.
        </p>
        </UsTicketNotice>
      )}
      {closeHolding && closeFit && closeFit.sell === null && (
        <p className="mt-1 break-words text-xs text-amber-500 light:text-amber-700" data-testid="us-ticket-close-too-small">
          {closeHolding.quantity} is below this market’s minimum order of {shownCloseRules?.minimumQuantity ?? '—'}, so it
          can’t be sold; it settles with the market.
        </p>
      )}
      {/* Only while the box still holds the preset: for a size the trader
          typed, "the close sells 12" would contradict the Closing line. */}
      {closeHolding && closeFit?.sell && closeFit.remainder !== '0'
        && decimalUnits(entry.quantity.trim()) === decimalUnits(closeFit.sell) && (
        <p className="mt-1 break-words text-xs text-amber-500 light:text-amber-700" data-testid="us-ticket-close-remainder">
          This market trades in steps of {shownCloseRules?.quantityStep ?? '—'}: the close sells {closeFit.sell};
          the remaining {closeFit.remainder} can’t be sold and settles with the market.
        </p>
      )}

      {!keepsTerms && listedBlockers.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-amber-500 light:text-amber-700" data-testid="us-ticket-blockers" aria-label="Why orders are unavailable">
          {listedBlockers.map((b) => <li key={b} className="break-words">{b}</li>)}
        </ul>
      )}

        <div className={cn('mt-3 grid min-w-0 grid-cols-1 gap-3',
          !lockedTarget && layout === 'page' && 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]')}>
          {!lockedTarget && (
            <UsMarketPicker markets={markets} selectedSlug={market.status === 'none' ? null : market.slug}
              onSelect={(slug) => void loadMarket(slug, false)} />
          )}

          <div className="min-w-0 space-y-3">
            <section ref={orderSection} aria-label="Order" className={cn("min-w-0", layout === 'page' && "border-border rounded border p-3")}>
              {keepsTerms && !selected && <UsTicketLoading railControls={railControls} quickSizes={sizePresets?.quickSizes} loading={market.status === 'loading'} />}
              {market.status === 'none' && <p className="text-muted-foreground text-xs">Choose a market to start an order.</p>}
              {/* The rail's host names the market and outcome visually; screen readers get them here. */}
              {layout === 'rail' && !editOrder && market.status !== 'none' && market.market && lockedTarget && (
                <p className="sr-only" data-testid="us-ticket-target">
                  {market.market.display.question ?? marketTitle(market.market.market)} — {lockedTarget.outcome.toUpperCase()}
                </p>
              )}
              {market.status !== 'none' && (layout === 'page' || editOrder) && (
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  {/* In the rail the host already names the exact market and outcome; an edit's host may not. */}
                  <h3 className={cn('min-w-0 break-words text-sm font-semibold [overflow-wrap:anywhere]', layout === 'rail' && !editOrder && 'sr-only')}>
                    {market.market ? (market.market.display.question ?? marketTitle(market.market.market)) : market.slug}
                  </h3>
                  <Tooltip content={HELP.refreshMarket} className="w-56 whitespace-normal">
                    <button type="button" className="border-border rounded border px-2 py-0.5 text-xs disabled:opacity-50"
                      disabled={market.status === 'loading' || committed} onClick={() => void loadMarket(market.slug, true)}>
                      {market.status === 'loading' ? 'Loading…' : 'Refresh market'}
                    </button>
                  </Tooltip>
                </div>
              )}
              {!keepsTerms && market.status === 'error' && (
                <p role="alert" className="mb-2 break-words text-xs" title={market.reason}>
                  Market unavailable: {usOrderReason(market.reason)}. No order can be checked against it; your entries are kept.
                </p>
              )}
              {selected && check && (
                <>
                  <fieldset disabled={committed || (!!editOrder && !editable)} className="min-w-0">
                    <UsTicketFields market={selected} entry={entry} shown={shown} check={check}
                      outcomeLocked={!!lockedTarget} actionLocked={!!editOrder || !!closeHolding}
                      termNotes={editOrder ? { ...editTermNotes(entry, editOrder), postOnlyHelp: editPostOnlyHelp(editOrder) } : undefined} closing={!!closeHolding} compact={layout === 'rail'} sizePresets={sizePresets}
                      permissions={permissions} inspectionOnly={institutional} onChange={edit} railControls={!editOrder && !closeHolding ? railControls : undefined} nowMs={selected ? usEvidenceNow(selected.timeEvidence, now) : now}
                      priceFlashing={priceFlashing} gtsPreferred={defaultExpiryMode === 'gts'} />
                  </fieldset>
                  {economics && <EconomicsRows economics={economics} priceUsd={entry.outcomePrice} quantity={entry.quantity}
                    outcome={entry.outcome ? entry.outcome.toUpperCase() : null} />}
                  {institutional && <dl data-testid="us-ticket-institutional-economics" className="border-border mt-3 space-y-1 rounded-lg border px-3 py-2 text-sm">
                    {(entry.action === 'sell' ? ['Price', 'Proceeds (fee)'] : ['Price', 'Total Cost (fee)', 'Max Payout', 'Max Profit']).map((label, index) =>
                      <div key={label} className="flex min-w-0 items-center justify-between gap-2">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="font-mono tabular-nums" title={index ? 'Native fees and account effects are not confirmed by a draft check.' : undefined}>
                          {index === 0 && !check.fields.price ? `${usdToCentsText(entry.outcomePrice.trim())}¢` : '—'}
                        </dd>
                      </div>)}
                  </dl>}
                  {institutional && !editOrder && !closeHolding ? (
                    <InstitutionalDraftCheck context={context} entry={entry} selected={selected} target={targetFor(selected, entry.outcome ?? 'yes')}
                      hostIdentity={JSON.stringify([priceHintSeq])} hostReadiness={hostMarketReadiness} blocked={!marketReady || marketBlockers.length > 0}
                      inspector={inspectInstitutionalDraft} now={now} clock={clock} className={ACTION} />
                  ) : closeOutcome ? (
                    <Tooltip content={CLOSE_HELP} wrapperClassName="relative block" className="w-64 whitespace-normal">
                      <p role={closeOutcome === 'sent' ? 'status' : 'alert'} data-testid="us-ticket-close-sent"
                        className={cn('mt-2 break-words text-xs', closeOutcome !== 'sent' && 'text-amber-500 light:text-amber-700')}>
                        {CLOSE_LINE[closeOutcome]}
                      </p>
                    </Tooltip>
                  ) : !editOrder ? (
                    // Routine submit mechanics do not cover the shared ticket's
                    // cost summary on hover or focus. Outcomes explain any action needed.
                    <ActionHelp help={keepsTerms ? '' : HELP.place} openOnFocus={!keepsTerms}>
                      <button type="button" data-testid="us-ticket-place" onClick={() => void placeOrder()}
                        disabled={stage !== 'idle' || blocked} className={ACTION}>
                        {stage === 'idle' ? 'Place Order' : 'Placing…'}
                      </button>
                    </ActionHelp>
                  ) : editable ? (
                    <>
                      <ActionHelp help={HELP.replace}>
                        <button type="button" data-testid="us-ticket-replace" onClick={() => void replaceOrder()}
                          disabled={stage !== 'idle' || blocked || unchanged} className={ACTION}>
                          {STAGE_LABEL[stage]}
                        </button>
                      </ActionHelp>
                      {unchanged && stage === 'idle' && (
                        <p className="text-muted-foreground mt-1 text-xs" data-testid="us-ticket-edit-unchanged">
                          {unknownTerms ? 'Nothing changed: price and size match the original.'
                            : 'Nothing changed: price, size and terms match the original.'}
                        </p>
                      )}
                    </>
                  ) : editPhase.kind === 'settlement' && editPhase.state === 'unconfirmed' ? (
                    <ActionHelp help={HELP.checkStatus}>
                      <button type="button" data-testid="us-ticket-edit-check" onClick={() => void checkEditStatus()}
                        disabled={stage !== 'idle'} className={ACTION}>
                        {stage === 'idle' ? 'Check status' : 'Checking…'}
                      </button>
                    </ActionHelp>
                  ) : editPhase.kind === 'canceled' || editPhase.kind === 'placing' ? (
                    <ActionHelp help={HELP.placeReplacement}>
                      <button type="button" data-testid="us-ticket-edit-place" onClick={() => { if (stageRef.current === 'idle') void placeReplacement(null); }}
                        disabled={editPhase.kind === 'placing' || stage !== 'idle' || blocked} className={ACTION}>
                        {editPhase.kind === 'canceled' && stage === 'idle' ? 'Place replacement' : stage === 'checking' ? 'Checking…' : 'Placing…'}
                      </button>
                    </ActionHelp>
                  ) : null}
                  {!keepsTerms && hostBlocker && (
                    <p role="status" data-testid="us-ticket-host-blocker"
                      className="mt-1 break-words text-xs text-amber-500 light:text-amber-700 [overflow-wrap:anywhere]">
                      {hostBlocker}
                    </p>
                  )}
                  {editStatus(editPhase) && (
                    <p role={editStatusWarns(editPhase) ? 'alert' : 'status'} data-testid="us-ticket-edit-status" title={editStatusCode(editPhase)}
                      className={cn('mt-1 break-words text-xs [overflow-wrap:anywhere]',
                        editStatusWarns(editPhase) && 'text-amber-500 light:text-amber-700')}>
                      {editStatus(editPhase)}
                    </p>
                  )}
                  {/* A close's unconfirmed outcome is said once, on its own line above. */}
                  {failure && !(closeOutcome === 'unconfirmed' && failure.kind === 'unconfirmed') && (
                    <UsTicketNotice className="mt-2" testId="us-ticket-failure" title={failure.kind === 'superseded' ? undefined : failure.code}
                      summary={failure.kind === 'unconfirmed' ? 'Result unconfirmed — check status before placing again.'
                        : failure.kind === 'edit-not-started' ? 'Edit not started — original order still resting.'
                          : 'Not sent — review order details.'}>
                    <p title={failure.kind === 'superseded' ? undefined : failure.code}>
                      {failure.kind === 'superseded'
                        ? 'Not sent: the order changed before it was placed. Your order is kept.'
                        : failure.kind === 'edit-not-started'
                          ? `Edit not started: ${usOrderReason(failure.code)}. Your original order is still resting.`
                        : failure.kind === 'not-sent'
                          // After a confirmed cancel the original is gone; only the entry is kept.
                          ? `Not sent: ${usOrderReason(failure.code)}.${editPhase.kind === 'canceled' || editPhase.kind === 'placing'
                            ? ' Your entry is kept; the original order is canceled.' : ' Your order is kept.'}`
                          : `Result unconfirmed: ${usOrderReason(failure.code)}. Check your saved US requests before placing another order.`}
                    </p>
                    </UsTicketNotice>
                  )}
                </>
              )}
              {/* Compact result by the action; uncertainty keeps its instruction visible. */}
              {layout === 'rail' && last && (
                <UsReceiptLine result={last.result} draft={last.draft} outcomeName={last.outcomeName} question={last.question} followed={followedRow} />
              )}
            </section>

            {layout === 'page' && last && (
              <UsTicketReceipt result={last.result} draft={last.draft} outcomeName={last.outcomeName} question={last.question} followed={followedRow} />
            )}
          </div>
        </div>
    </section>
  );
}
